import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Plus, Pencil, Trash2, Save, X, Search,
    Loader2, Factory, AlertTriangle, Cpu, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const TYPE_CONFIG = {
    'Аксиома-03': { icon: Cpu, color: 'text-blue-400', bg: 'bg-blue-500/15' },
    'Аксиома-01': { icon: Cpu, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
    'Карат-307': { icon: Cpu, color: 'text-amber-400', bg: 'bg-amber-500/15' }
};

const DeviceGroupsTab = ({ userInfo }) => {
    const navigate = useNavigate();
    const canChangeGroups = userInfo?.canChangeObjectsGroups === 1;

    const [groups, setGroups] = useState([]);
    const [availableDevices, setAvailableDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', devices: [] });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [deviceSearch, setDeviceSearch] = useState('');

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const loadData = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [groupsRes, devicesRes] = await Promise.all([
                axios.get(`${API_URL}/api/devicegroups`, authHeaders()),
                axios.get(`${API_URL}/api/devicegroups/available-devices`, authHeaders())
            ]);
            setGroups(groupsRes.data);
            setAvailableDevices(devicesRes.data);
        } catch (err) {
            console.error('Ошибка загрузки:', err);
            setLoadError('Не удалось загрузить данные. Проверьте подключение к серверу.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const deviceByName = useMemo(() => {
        const map = {};
        availableDevices.forEach(d => { map[d.name] = d; });
        return map;
    }, [availableDevices]);

    const filteredDevices = useMemo(() => {
        const q = deviceSearch.trim().toLowerCase();
        if (!q) return availableDevices;
        return availableDevices.filter(d =>
            [d.name, d.city, d.street, d.deviceType].join(' ').toLowerCase().includes(q)
        );
    }, [availableDevices, deviceSearch]);

    const openCreateForm = () => {
        setEditingId(null);
        setFormData({ name: '', description: '', devices: [] });
        setDeviceSearch('');
        setFormError('');
        setIsFormOpen(true);
    };

    const openEditForm = (group) => {
        setEditingId(group.id);
        setFormData({
            name: group.name,
            description: group.description || '',
            devices: [...group.devices]
        });
        setDeviceSearch('');
        setFormError('');
        setIsFormOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setIsFormOpen(false);
        setEditingId(null);
        setFormError('');
    };

    const toggleDevice = (name) => {
        setFormData(prev => ({
            ...prev,
            devices: prev.devices.includes(name)
                ? prev.devices.filter(n => n !== name)
                : [...prev.devices, name]
        }));
    };

    const openInDevicesPage = (device) => {
        localStorage.setItem('selectedDeviceFromMap', JSON.stringify(device));
        navigate('/devices');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving || !canChangeGroups) return;
        if (!formData.name.trim()) {
            setFormError('Введите название группы');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const payload = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                devices: formData.devices
            };
            if (editingId == null) {
                await axios.post(`${API_URL}/api/devicegroups`, payload, authHeaders());
            } else {
                await axios.put(`${API_URL}/api/devicegroups/${editingId}`, payload, authHeaders());
            }
            setIsFormOpen(false);
            setEditingId(null);
            await loadData();
        } catch (err) {
            console.error('Ошибка сохранения группы:', err);
            setFormError(err.response?.data?.message || 'Не удалось сохранить группу');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/api/devicegroups/${deleteTarget.id}`, authHeaders());
            if (editingId === deleteTarget.id) {
                setIsFormOpen(false);
                setEditingId(null);
            }
            setDeleteTarget(null);
            await loadData();
        } catch (err) {
            console.error('Ошибка удаления группы:', err);
            setLoadError(err.response?.data?.message || 'Не удалось удалить группу');
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
                <Loader2 className="w-6 h-6 animate-spin" />
                Загрузка данных…
            </div>
        );
    }

    const renderForm = (formKey) => (
        <div
            key={formKey}
            className="bg-slate-800 border border-blue-500 rounded-xl shadow-lg shadow-blue-500/10 animate-slide-down-fade"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
                <h3 className="text-white font-semibold flex items-center gap-2">
                    {editingId == null ? (
                        <>
                            <Plus className="w-5 h-5 text-blue-400" />
                            Создание группы
                        </>
                    ) : (
                        <>
                            <Pencil className="w-5 h-5 text-blue-400" />
                            Редактирование группы
                        </>
                    )}
                </h3>
                <button
                    onClick={closeForm}
                    disabled={saving}
                    className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700 transition"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                            Название группы *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Например: Северный район"
                            required
                            disabled={saving}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                            Описание
                        </label>
                        <input
                            type="text"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Необязательно"
                            disabled={saving}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                        />
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                        <label className="block text-sm font-medium text-slate-300">
                            Объекты учета{' '}
                            <span className="text-blue-400">
                                (выбрано: {formData.devices.length})
                            </span>
                        </label>
                        <div className="flex gap-3 text-xs">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({
                                    ...prev,
                                    devices: [
                                        ...new Set([
                                            ...prev.devices,
                                            ...filteredDevices.map(d => d.name)
                                        ])
                                    ]
                                }))}
                                disabled={saving}
                                className="text-blue-400 hover:text-blue-300"
                            >
                                Выбрать все
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, devices: [] }))}
                                disabled={saving}
                                className="text-slate-400 hover:text-white"
                            >
                                Очистить
                            </button>
                        </div>
                    </div>

                    <div className="relative mb-2">
                        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={deviceSearch}
                            onChange={(e) => setDeviceSearch(e.target.value)}
                            placeholder="Поиск по названию, адресу, типу…"
                            disabled={saving}
                            className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                        />
                    </div>

                    <div className="max-h-80 overflow-y-auto border border-slate-600 rounded-lg divide-y divide-slate-700">
                        {filteredDevices.length === 0 ? (
                            <div className="p-4 text-center text-slate-500 text-sm">
                                Обслуживаемые устройства не найдены
                            </div>
                        ) : (
                            filteredDevices.map(device => (
                                <label
                                    key={device.name}
                                    className="flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 cursor-pointer transition"
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.devices.includes(device.name)}
                                        onChange={() => toggleDevice(device.name)}
                                        disabled={saving}
                                        className="w-4 h-4 rounded accent-blue-500 flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white font-medium truncate flex items-center gap-2">
                                            {[device.city, device.street, device.houseNumber]
                                                .filter(Boolean).join(', ')}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            {device.name}
                                        </div>
                                    </div>
                                    <span className="text-xs text-slate-400 flex-shrink-0">
                                        {device.deviceType}
                                    </span>
                                </label>
                            ))
                        )}
                    </div>
                </div>

                {formError && (
                    <div className="px-3 py-2 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center animate-slide-down-fade">
                        <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>{formError}</span>
                    </div>
                )}

                <div className="flex gap-3 pt-1">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg transition text-sm flex items-center justify-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Сохранение…
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                {editingId == null ? 'Создать группу' : 'Сохранить изменения'}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={closeForm}
                        disabled={saving}
                        className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-300 hover:text-white font-medium rounded-lg transition text-sm"
                    >
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div className="space-y-4">
            {loadError && (
                <div className="px-4 py-3 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center justify-between animate-slide-down-fade">
                    <span className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        {loadError}
                    </span>
                    <button onClick={() => setLoadError('')} className="text-red-300 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-lg font-semibold text-white">Группы объектов учета</h2>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Доступные устройства — только из списка «Обслуживаемые»
                    </p>
                </div>
                {canChangeGroups && (
                    <button
                        onClick={openCreateForm}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        Новая группа
                    </button>
                )}
            </div>

            {!canChangeGroups && (
                <div className="px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-400 text-sm">
                    Нет прав для создания и изменения групп
                </div>
            )}

            <div className="space-y-3">
                {isFormOpen && canChangeGroups && editingId === null && renderForm('form-new')}

                {groups.length === 0 ? (
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 text-center text-slate-500 text-sm">
                        Группы пока не созданы
                    </div>
                ) : (
                    groups.map(group => (
                        <React.Fragment key={group.id}>
                            <div
                                className={`bg-slate-800 border rounded-xl p-4 transition ${
                                    editingId === group.id
                                        ? 'border-blue-500'
                                        : 'border-slate-700 hover:border-slate-600'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <h3 className="text-white font-semibold truncate">{group.name}</h3>
                                        {group.description && (
                                            <p className="text-slate-400 text-xs mt-1">{group.description}</p>
                                        )}
                                        <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded-full text-xs">
                                            <Factory className="w-3 h-3" />
                                            {group.devices.length} устройств
                                        </span>
                                    </div>
                                    {canChangeGroups && (
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button
                                                onClick={() => openEditForm(group)}
                                                className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-600/20 rounded-lg transition"
                                                title="Редактировать"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(group)}
                                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-600/20 rounded-lg transition"
                                                title="Удалить"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {group.devices.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-slate-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                        {group.devices.map(name => {
                                            const d = deviceByName[name];
                                            const cfg = TYPE_CONFIG[d?.deviceType] || TYPE_CONFIG['Аксиома-03'];
                                            const address = d ? [d.city, d.street, d.houseNumber]
                                                .filter(Boolean).join(', ') : '—';

                                            return (
                                                <div key={name} className="bg-slate-700/30 rounded-lg p-2.5 space-y-1.5">
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <Factory className="w-3 h-3 text-slate-500 flex-shrink-0" />
                                                        <span className="text-slate-300 truncate">
                                                            {address}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-white text-sm font-medium truncate">
                                                            {d?.name ?? name}
                                                            {d?.deviceType && (
                                                                <span className={`ml-1 font-normal ${cfg.color}`}>
                                                                    ({d.deviceType})
                                                                </span>
                                                            )}
                                                        </span>
                                                        {d && (
                                                            <button
                                                                onClick={() => openInDevicesPage(d)}
                                                                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-600/20 rounded transition flex-shrink-0"
                                                                title="Открыть в «Объекте учета»"
                                                            >
                                                                <ExternalLink className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {isFormOpen && canChangeGroups && editingId === group.id && renderForm(`form-${group.id}`)}
                        </React.Fragment>
                    ))
                )}
            </div>

            {deleteTarget && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
                        onClick={() => !deleting && setDeleteTarget(null)}
                    />
                    <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-[scaleIn_0.25s_cubic-bezier(0.16,1,0.3,1)]">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-600/20 flex items-center justify-center">
                            <Trash2 className="w-6 h-6 text-red-400" />
                        </div>
                        <h3 className="text-white font-semibold text-center">Удалить группу?</h3>
                        <p className="text-slate-400 text-sm text-center mt-2">
                            Группа <span className="text-white font-medium">«{deleteTarget.name}»</span> и все
                            привязки устройств ({deleteTarget.devices.length}) будут удалены безвозвратно.
                        </p>
                        <div className="flex gap-3 mt-5">
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-medium rounded-lg transition text-sm flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Удалить
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-300 hover:text-white font-medium rounded-lg transition text-sm"
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeviceGroupsTab;